/**
 * DirectiveSigningKeySection — "Directive signing key": the workspace's own
 * HMAC key for signing `directive`-tagged Team Log messages, replacing the
 * single global DIRECTIVE_SIGNING_KEY env var during the transition
 * (track:workspace-directive-keys-a, spec #48992, decision #48907).
 *
 * GETs /api/workspace/directive-signing-key (any confirmed member — Lead
 * or partner, deliberately wider than McpConnectionSection's Lead-only
 * gate, per decision #48907's own trust-boundary paragraph). Unlike
 * MyDaemonKeySection's show-once pattern, this key stays visible on every
 * load — decision #48907 explicitly asks for that, since it's a shared
 * workspace secret a Lead may need to reference again, not a one-time
 * personal credential. Only the Regenerate button is Lead-only, gated the
 * same client-side way HumanMembersSection gates its Invite button
 * (current user's own membership row, from useWorkspaceMembers).
 */
import { useEffect, useState } from "react";
import { getAuthHeader } from "../../lib/auth";
import { auth } from "../../lib/firebase";
import { useWorkspaceMembers } from "../../hooks";
import { CopyInstructionsButton } from "../CopyInstructionsButton";
import { buildDirectiveSigningKeyInstructions } from "../../lib/copyInstructions";

interface DirectiveSigningKeyResponse {
  directiveSigningKey: string;
  error?: string;
}

export function DirectiveSigningKeySection({ workspaceId = "default" }: { workspaceId?: string }) {
  const [key, setKey] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const { members } = useWorkspaceMembers(workspaceId);
  const currentUserIsLead = members.some(
    (m) => m.user_id === auth.currentUser?.uid && m.role === "human_lead",
  );

  async function load() {
    try {
      const headers = await getAuthHeader(workspaceId);
      const res = await fetch("/api/workspace/directive-signing-key", { headers });
      if (res.status === 403) {
        setForbidden(true);
        return;
      }
      const data: DirectiveSigningKeyResponse = await res.json();
      if (!res.ok) {
        setError(data.error || `HTTP ${res.status}`);
        return;
      }
      setKey(data.directiveSigningKey);
      setForbidden(false);
      setError(null);
    } catch (err) {
      console.error("[DirectiveSigningKeySection] Failed to load key:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  async function handleRegenerate() {
    if (!confirm(
      "This replaces the workspace's directive signing key. Every daemon in this workspace must be restarted to pick up the new one — until then, its directive dispatches may fail to verify. Continue?"
    )) {
      return;
    }
    setRegenerating(true);
    setError(null);
    try {
      const headers = await getAuthHeader(workspaceId);
      const res = await fetch("/api/workspace/directive-signing-key/regenerate", {
        method: "POST",
        headers,
      });
      const data: DirectiveSigningKeyResponse = await res.json();
      if (!res.ok) {
        setError(data.error || `HTTP ${res.status}`);
        return;
      }
      setKey(data.directiveSigningKey);
      setCopied(false);
    } catch (err) {
      console.error("[DirectiveSigningKeySection] Failed to regenerate key:", err);
      setError("Network error. Please try again.");
    } finally {
      setRegenerating(false);
    }
  }

  function handleCopy() {
    if (!key) return;
    navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <section id="team-section-directive-signing-key" style={{ marginBottom: 32 }}>
      <div className="sc-section-label">Directive Signing Key</div>
      <div style={{ fontSize: 13, color: "var(--muted)", fontFamily: "var(--font-ui)", marginBottom: 12 }}>
        Shared by the whole workspace. Relay signs every work order with it, and each helper checks the signature before starting an AI partner. Relay Helper and <code>relay start</code> fetch it automatically, so you don't need to copy it. Copy it only when setting up a helper by hand with <code>relay install --directive-signing-key</code>. A Lead regenerates it if it is ever exposed, and helpers pick up the new one within 10 minutes.
      </div>

      {loading && (
        <div style={{ padding: "1rem", color: "var(--muted)", fontSize: 14, fontFamily: "var(--font-ui)" }}>
          Loading directive signing key...
        </div>
      )}

      {!loading && forbidden && (
        <div className="sc-callout info">
          Only a confirmed workspace member can view the directive signing key.
        </div>
      )}

      {!loading && !forbidden && error && <div className="sc-callout error">{error}</div>}

      {!loading && !forbidden && !error && key && (
        <>
          <div
            className="sc-item-row"
            style={{ marginBottom: 8, fontFamily: "var(--font-mono)", fontSize: 13, wordBreak: "break-all", color: "var(--ink)", display: "block" }}
          >
            {key}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <button className="btn sm" onClick={handleCopy}>
              {copied ? "✓ Copied!" : "Copy key"}
            </button>
            {currentUserIsLead && (
              <button className="btn sm" onClick={handleRegenerate} disabled={regenerating}>
                {regenerating ? "Regenerating..." : "Regenerate"}
              </button>
            )}
            <CopyInstructionsButton text={buildDirectiveSigningKeyInstructions()} />
          </div>
        </>
      )}
    </section>
  );
}
